import { ReadonlyVec2 } from 'gl-matrix';
import { enqueueSnackbar } from '~/utils/notifications';
import { Environment } from '~/engine/data/Environment';
import { targetCrosshairCursor } from '~/engine/data/common/customs/cursors/targetCrosshairCursor';
import { ImageObject } from '~/engine/data/image/ImageObject';
import { RegionOfInterest } from '~/engine/data/structure/RegionOfInterest';
import {
    EventHandlerResult,
    EventHandlerResultType,
} from '~/engine/tool/EventHandlerResult';
import { ModifierKeys } from '~/engine/tool/ModifierKeys';
import { LayerBit } from '~/engine/view/layers/LayerBit';
import type { EnvironmentViews } from '~/engine/view/EnvironmentViews';
import { TextureView } from '~/engine/view/TextureView';
import { ViewManager } from '~/engine/view/ViewManager';
import { PlaneView } from '~/engine/view/views/PlaneView';
import { LineDrawActionState } from '../../actions/contouring-actions/LineDrawAction';
import {
    DrawingConstraints,
    intensityStrokeRefusal,
    strokeFollowsThreshold,
} from '../../actions/DrawActionState';
import { MOUSE_BUTTON_LEFT, MOUSE_BUTTON_RIGHT } from '../../MouseButton';
import { CrosshairInteractionMode, Tool } from '../../Tool';
import { writeRefusal } from './editableView';
import { localization } from '~/i18n';
import { isViewDrawable } from './obliqueGating';

/**
 * Draws or erases a closed polyline contour on the acquired slices of the
 * armed environment's primary image. A line binds to the view its first
 * point is placed in and accepts every further point from that view alone,
 * until the line closes or is discarded; the next line may start in any
 * other drawable view of the same environment. A press that cannot start a
 * line warns with the reason ({@link refusalFor}).
 */
export class LineDrawTool extends Tool {
    public readonly toolType = 'line';
    private readonly image: ImageObject;
    private readonly structure: RegionOfInterest;
    private readonly constraints?: DrawingConstraints;
    private readonly isErasing: boolean;
    protected _drawState?: LineDrawActionState;

    public override isActionInProgress(): boolean {
        return this._drawState?.drawing === true;
    }

    public override getDrawingView(): PlaneView | undefined {
        return this.isActionInProgress() ? this._drawState?.view : undefined;
    }

    /** A line bound to `view` is discarded at once, and says so. */
    public override cancelActionIn(view: PlaneView): EventHandlerResult {
        if (this.getDrawingView() !== view) {
            return new EventHandlerResult(
                EventHandlerResultType.EVENT_NOT_HANDLED
            );
        }
        return this.discardLineWithWarning();
    }

    public override isStrokeInProgress(): boolean {
        return this._drawState?.mouseHeld === true;
    }

    public override getCrosshairInteractionMode(
        isDrawingActiveInOtherView: boolean
    ): CrosshairInteractionMode {
        return Tool.contouringCrosshairMode(
            isDrawingActiveInOtherView,
            this.isStrokeInProgress()
        );
    }

    public override usesButtonDuringAction(button: number): boolean {
        return button === MOUSE_BUTTON_RIGHT && this.isActionInProgress();
    }

    public constructor(
        image: ImageObject,
        structure: RegionOfInterest,
        mode: 'draw' | 'erase',
        private readonly environment: Environment | undefined,
        private readonly views: EnvironmentViews | undefined,
        constraints?: DrawingConstraints
    ) {
        super();
        this.image = image;
        this.structure = structure;
        this.constraints = constraints;
        this.isErasing = mode === 'erase';
    }

    /**
     * A view of another environment, or with none armed, gets the
     * refusal of {@link writeRefusal}; a view of the armed
     * environment that does not show the acquired image slices (another
     * plane, or a turned view) the `contouring.refused.lineOffAcquiredSlice`
     * reason;
     * and, while a drawn line keeps to the threshold band, a view that does
     * not show the primary image the refusal of
     * {@link intensityStrokeRefusal}.
     */
    public override refusalFor(view: PlaneView): string | undefined {
        return writeRefusal(view, this.environment, (own) => {
            if (!isViewDrawable(own)) {
                return localization.t(
                    'contouring.refused.lineOffAcquiredSlice'
                );
            }
            return strokeFollowsThreshold(this.constraints, this.isErasing)
                ? intensityStrokeRefusal(own)
                : undefined;
        });
    }

    /**
     * A plus crosshair when drawing, a minus crosshair when erasing; none on
     * a structure whose contours cannot be modified. A view a line may not
     * start in shows the manager's refusal cursor instead.
     */
    public override get cursor(): string | undefined {
        if (!this.structure.canModifyContours) {
            return undefined;
        }
        return targetCrosshairCursor(this.isErasing);
    }

    /**
     * Whether the view a line is bound to can still take its points: it
     * still shows the armed environment and still draws on a slice. A view
     * turned in place under the line draws on another plane; a view that
     * left the environment's set (a tile remounted on another plane) is
     * dead. Either way a further point would not land on the slice the line
     * was started on.
     */
    private canContinueIn(view: PlaneView): boolean {
        const present = !this.views || this.views.current.includes(view);
        return present && isViewDrawable(view);
    }

    /**
     * Discards the line in progress when its bound view can no longer take
     * its points, and says so. Returns the repaint that clears the preview,
     * or undefined when nothing was dropped.
     */
    private discardStaleLine(): EventHandlerResult | undefined {
        if (
            !this._drawState ||
            !this.isActionInProgress() ||
            this.canContinueIn(this._drawState.view)
        ) {
            return undefined;
        }
        return this.discardLineWithWarning();
    }

    /**
     * Discards the line in progress with the
     * `contouring.refused.lineOffStartSlice` warning. Returns the repaint
     * that clears the preview.
     */
    private discardLineWithWarning(): EventHandlerResult {
        this._drawState?.discardDrawing();
        enqueueSnackbar(
            localization.t('contouring.refused.lineOffStartSlice'),
            { variant: 'warning' }
        );
        return this.lineDiscardedResult();
    }

    /** The handled result that repaints the contour layer a dropped line previewed on. */
    private lineDiscardedResult(): EventHandlerResult {
        return new EventHandlerResult(
            EventHandlerResultType.EVENT_HANDLED,
            true,
            {
                layers: LayerBit.CONTOUR_LAYER_BIT,
                volume: this.structure.boundingBox,
            }
        );
    }

    /**
     * The view the current line accepts points from, or the view a new line
     * would bind to when none is in progress.
     */
    private isDrawingView(view: PlaneView): boolean {
        return this.isActionInProgress()
            ? this._drawState?.view === view
            : this.refusalFor(view) === undefined;
    }

    /**
     * Whether a line is in progress and `view` is not the view it is bound
     * to. Such a view takes no point, move, left release or double-click of
     * the line: a position on it lies outside the part of the slice the
     * bound view shows, so the line stays open on its bound view.
     */
    private isOffBoundView(view: TextureView | undefined): boolean {
        return this.isActionInProgress() && view !== this._drawState?.view;
    }

    public onMouseMove(
        coordinates: ReadonlyVec2,
        buttons: number,
        view: TextureView
    ): EventHandlerResult {
        const discarded = this.discardStaleLine();
        if (discarded) {
            return discarded;
        }
        if (this.isOffBoundView(view)) {
            return new EventHandlerResult(EventHandlerResultType.EVENT_HANDLED);
        }
        if (this._drawState) {
            return this._drawState.onMouseMove(coordinates, buttons, view);
        }
        return new EventHandlerResult(EventHandlerResultType.EVENT_NOT_HANDLED);
    }

    public onMousePress(
        button: number,
        modifierKeys: ModifierKeys,
        coordinates: ReadonlyVec2,
        view: PlaneView
    ): EventHandlerResult {
        if (this.structure.canModifyContours === false) {
            this.resetDrawAction(view);
            enqueueSnackbar(
                'Modifying approved structures is not allowed. Unapprove the structure to make changes.',
                { variant: 'warning' }
            );
            return new EventHandlerResult(
                EventHandlerResultType.EVENT_NOT_HANDLED
            );
        }
        const discarded = this.discardStaleLine();
        if (discarded) {
            return discarded;
        }
        if (button === MOUSE_BUTTON_LEFT && this.isDrawingView(view)) {
            view.snapToImagePixel();
            if (this._drawState?.view !== view) {
                this.resetDrawAction(view);
            }

            this._drawState?.onMousePress(modifierKeys, coordinates, view);
            return new EventHandlerResult(EventHandlerResultType.EVENT_HANDLED);
        }
        const refusal =
            button === MOUSE_BUTTON_LEFT && !this.isActionInProgress()
                ? this.refusalFor(view)
                : undefined;
        if (refusal !== undefined) {
            enqueueSnackbar(refusal, { variant: 'warning' });
            return new EventHandlerResult(
                EventHandlerResultType.EVENT_NOT_HANDLED
            );
        }

        // Block other actions during free-form drawing, and block
        // panning in every view other than the tile the line is bound
        // to while a line is drawn point by point.
        if (this.isStrokeInProgress() || this.isOffBoundView(view)) {
            return new EventHandlerResult(EventHandlerResultType.EVENT_HANDLED);
        }
        return new EventHandlerResult(EventHandlerResultType.EVENT_NOT_HANDLED);
    }

    public onMouseRelease(
        button: number,
        coordinates: ReadonlyVec2,
        view?: TextureView
    ): EventHandlerResult {
        if (button === MOUSE_BUTTON_LEFT) {
            if (this._drawState) {
                if (this.isOffBoundView(view)) {
                    this._drawState.endHold();
                    return new EventHandlerResult(
                        EventHandlerResultType.EVENT_HANDLED
                    );
                }
                const result = this._drawState.onMouseRelease(
                    coordinates,
                    view
                );
                ViewManager.invalidate(
                    LayerBit.CONTOUR_LAYER_BIT,
                    this.structure.boundingBox
                );
                return result;
            }
        }
        if (
            button === MOUSE_BUTTON_RIGHT &&
            this.isActionInProgress() &&
            !this.isStrokeInProgress()
        ) {
            if (this.isOffBoundView(view)) {
                return new EventHandlerResult(
                    EventHandlerResultType.EVENT_HANDLED
                );
            }
            if (!this._drawState?.undoLastPoint()) {
                this._drawState?.discardDrawing();
                return this.lineDiscardedResult();
            }
            return new EventHandlerResult(EventHandlerResultType.EVENT_HANDLED);
        }
        return new EventHandlerResult(EventHandlerResultType.EVENT_NOT_HANDLED);
    }

    public onMouseWheel(
        _delta: 1 | -1,
        _modifierKeys: ModifierKeys,
        _coordinates: ReadonlyVec2,
        _view: PlaneView
    ): EventHandlerResult {
        return new EventHandlerResult(EventHandlerResultType.EVENT_NOT_HANDLED);
    }

    public onMouseDoubleClick(
        coordinates: ReadonlyVec2,
        view: PlaneView
    ): EventHandlerResult {
        if (this.isOffBoundView(view)) {
            return new EventHandlerResult(EventHandlerResultType.EVENT_HANDLED);
        }
        if (this._drawState) {
            return this._drawState.finishOnPoint(coordinates);
        }
        return new EventHandlerResult(EventHandlerResultType.EVENT_NOT_HANDLED);
    }

    public override onKeyPress(
        key: string,
        _modifierKeys: ModifierKeys,
        _view: TextureView
    ): EventHandlerResult {
        if (key === 'Escape' && this.isActionInProgress()) {
            this._drawState?.discardDrawing();
            return this.lineDiscardedResult();
        }
        return new EventHandlerResult(EventHandlerResultType.EVENT_NOT_HANDLED);
    }

    protected resetDrawAction(view: TextureView) {
        if (view instanceof PlaneView) {
            this._drawState?.dispose();
            this._drawState = new LineDrawActionState(
                view,
                this.structure,
                this.image,
                this.isErasing,
                this.constraints
            );
        }
    }

    public onDeactivate(): EventHandlerResult {
        let result: EventHandlerResult | undefined;
        if (this._drawState) {
            result = this._drawState.stopAction();
            this._drawState.dispose();
            this._drawState = undefined;
        }

        return (
            result ??
            new EventHandlerResult(EventHandlerResultType.EVENT_HANDLED)
        );
    }
}
